/**
 * AdvertiserCreativeUploadPage — landing shell (OPUS DASH-146).
 *
 * Advertiser portal page for registering creative assets (banner / video /
 * HTML) and watching the approval state set by internal ops. Surfaces:
 *
 *   - Upload form         (advertiser id, uploaded by, kind radio,
 *                          name, width, height, url, size)
 *   - Assets gallery      (list of previously uploaded assets with an
 *                          approval-state badge + delete)
 *
 * No @tanstack/react-query — the page renders the gallery via a single
 * `useState` + `useEffect` hydration pattern so the SSR snapshot the test
 * harness uses stays deterministic.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
	type CreativeAsset,
	type CreativeKind,
	type UploadFormData,
	advertiserCreativeUploadPaths,
	approvalBadgeLabel,
	CREATIVE_KINDS,
	createAsset,
	deleteAsset,
	fetchAssets,
	formatBytes,
	formatDimensions,
	setAssetApproval,
	uploadFormSchema,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — zinc borders, 6-8px rounded, light theme.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"inline-flex items-center justify-center rounded-[6px] border border-green-700 bg-green-700 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-green-800 hover:border-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"inline-flex items-center justify-center rounded-[6px] border border-red-300 bg-white px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export function AdvertiserCreativeUploadPage() {
	const [advertiserFocus, setAdvertiserFocus] = useState<string>("");
	const [refreshKey, setRefreshKey] = useState(0);

	const onCreated = useCallback((asset: CreativeAsset) => {
		setAdvertiserFocus(asset.advertiser_id);
		setRefreshKey((k) => k + 1);
	}, []);

	return (
		<main className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-14">
			<Breadcrumb />
			<Heading />
			<UploadCard onCreated={onCreated} />
			<GalleryCard
				advertiserId={advertiserFocus}
				onAdvertiserChange={setAdvertiserFocus}
				refreshKey={refreshKey}
				triggerRefresh={() => setRefreshKey((k) => k + 1)}
			/>
			<DocHint />
		</main>
	);
}

export default AdvertiserCreativeUploadPage;

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-500">Advertisers</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Creative upload</li>
			</ol>
		</nav>
	);
}

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Advertisers
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Creative upload
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Register banner, video, or HTML creative assets on behalf of an
				advertiser. Uploaded assets enter a review queue — internal ops flip
				the approval state before they can be attached to a campaign.
			</p>
		</header>
	);
}

// ---------------------------------------------------------------------------
// Upload form
// ---------------------------------------------------------------------------

interface UploadFormState {
	advertiserId: string;
	uploadedBy: string;
	kind: CreativeKind;
	name: string;
	url: string;
	widthRaw: string;
	heightRaw: string;
	sizeBytesRaw: string;
	errors: Record<string, string>;
	submitting: boolean;
	success: string | null;
	serverError: string | null;
}

const EMPTY_UPLOAD: UploadFormState = {
	advertiserId: "",
	uploadedBy: "",
	kind: "banner",
	name: "",
	url: "",
	widthRaw: "",
	heightRaw: "",
	sizeBytesRaw: "",
	errors: {},
	submitting: false,
	success: null,
	serverError: null,
};

function UploadCard(props: { onCreated: (asset: CreativeAsset) => void }) {
	const [state, setState] = useState<UploadFormState>(EMPTY_UPLOAD);

	const onSubmit = useCallback(
		async (event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			const parsed = uploadFormSchema.safeParse({
				advertiserId: state.advertiserId,
				uploadedBy: state.uploadedBy,
				kind: state.kind,
				name: state.name,
				url: state.url,
				widthRaw: state.widthRaw,
				heightRaw: state.heightRaw,
				sizeBytesRaw: state.sizeBytesRaw,
			});
			if (!parsed.success) {
				setState((prev) => ({
					...prev,
					errors: parsed.errors,
					success: null,
					serverError: null,
				}));
				return;
			}
			setState((prev) => ({
				...prev,
				errors: {},
				submitting: true,
				success: null,
				serverError: null,
			}));
			try {
				const data: UploadFormData = parsed.data;
				const asset = await createAsset({
					advertiser_id: data.advertiser_id,
					uploaded_by: data.uploaded_by,
					kind: data.kind,
					name: data.name,
					url: data.url,
					size_bytes: data.size_bytes ?? undefined,
					width: data.width ?? undefined,
					height: data.height ?? undefined,
				});
				setState({
					...EMPTY_UPLOAD,
					advertiserId: data.advertiser_id,
					uploadedBy: data.uploaded_by,
					success: `Registered ${asset.name} for ${asset.advertiser_id}.`,
				});
				props.onCreated(asset);
			} catch (err) {
				const msg =
					err instanceof Error ? err.message : "Unable to register asset.";
				setState((prev) => ({
					...prev,
					submitting: false,
					serverError: msg,
				}));
			}
		},
		[state, props],
	);

	return (
		<section
			aria-label="Register creative asset"
			className={`${CARD} flex flex-col gap-4 p-6`}
		>
			<header className="flex flex-col gap-1">
				<h2 className="text-lg font-semibold text-zinc-900">
					Register a new asset
				</h2>
				<p className={HINT}>
					Posts to
					<span className="font-mono text-zinc-700">
						{" "}
						POST {advertiserCreativeUploadPaths.root}
					</span>
					. The URL is the output of a presigned-upload stub.
				</p>
			</header>
			<form
				onSubmit={onSubmit}
				className="flex flex-col gap-3"
				noValidate
				aria-label="Creative upload form"
			>
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
					<Field
						id="adv-creative-advertiser"
						name="advertiserId"
						label="Advertiser ID"
						placeholder="adv_42"
						value={state.advertiserId}
						error={state.errors.advertiserId}
						disabled={state.submitting}
						onChange={(v) =>
							setState((prev) => ({
								...prev,
								advertiserId: v,
								errors: { ...prev.errors, advertiserId: "" },
							}))
						}
					/>
					<Field
						id="adv-creative-uploader"
						name="uploadedBy"
						label="Uploaded by"
						placeholder="user_42"
						value={state.uploadedBy}
						error={state.errors.uploadedBy}
						disabled={state.submitting}
						onChange={(v) =>
							setState((prev) => ({
								...prev,
								uploadedBy: v,
								errors: { ...prev.errors, uploadedBy: "" },
							}))
						}
					/>
				</div>

				<fieldset
					className="flex flex-col gap-2"
					aria-describedby="adv-creative-kind-hint"
				>
					<legend className={LABEL}>Kind</legend>
					<div
						className="flex flex-wrap gap-2"
						role="radiogroup"
						aria-label="Creative kind"
					>
						{CREATIVE_KINDS.map((kind) => {
							const checked = state.kind === kind;
							return (
								<label
									key={kind}
									className={`inline-flex cursor-pointer items-center gap-2 rounded-[6px] border px-3 py-1.5 text-sm capitalize ${
										checked
											? "border-green-600 bg-green-50 text-green-900"
											: "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50"
									}`}
								>
									<input
										type="radio"
										name="kind"
										value={kind}
										checked={checked}
										onChange={() =>
											setState((prev) => ({
												...prev,
												kind,
												errors: { ...prev.errors, kind: "" },
											}))
										}
										disabled={state.submitting}
										className="sr-only"
										aria-label={`Kind: ${kind}`}
									/>
									{kind}
								</label>
							);
						})}
					</div>
					{state.errors.kind && (
						<p className={ERROR_TEXT}>{state.errors.kind}</p>
					)}
					<p id="adv-creative-kind-hint" className={HINT}>
						Banner assets store pixel width + height. Video + HTML leave those
						blank.
					</p>
				</fieldset>

				<Field
					id="adv-creative-name"
					name="name"
					label="Name"
					placeholder="Summer promo banner"
					value={state.name}
					error={state.errors.name}
					disabled={state.submitting}
					onChange={(v) =>
						setState((prev) => ({
							...prev,
							name: v,
							errors: { ...prev.errors, name: "" },
						}))
					}
				/>

				<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
					<Field
						id="adv-creative-width"
						name="widthRaw"
						label="Width (px)"
						type="number"
						placeholder="728"
						value={state.widthRaw}
						error={state.errors.width}
						disabled={state.submitting}
						onChange={(v) =>
							setState((prev) => ({
								...prev,
								widthRaw: v,
								errors: { ...prev.errors, width: "" },
							}))
						}
					/>
					<Field
						id="adv-creative-height"
						name="heightRaw"
						label="Height (px)"
						type="number"
						placeholder="90"
						value={state.heightRaw}
						error={state.errors.height}
						disabled={state.submitting}
						onChange={(v) =>
							setState((prev) => ({
								...prev,
								heightRaw: v,
								errors: { ...prev.errors, height: "" },
							}))
						}
					/>
					<Field
						id="adv-creative-size"
						name="sizeBytesRaw"
						label="Size (bytes)"
						type="number"
						placeholder="45000"
						value={state.sizeBytesRaw}
						error={state.errors.sizeBytes}
						disabled={state.submitting}
						onChange={(v) =>
							setState((prev) => ({
								...prev,
								sizeBytesRaw: v,
								errors: { ...prev.errors, sizeBytes: "" },
							}))
						}
					/>
				</div>

				<Field
					id="adv-creative-url"
					name="url"
					label="Asset URL"
					placeholder="https://cdn.example.com/assets/summer.png"
					value={state.url}
					error={state.errors.url}
					disabled={state.submitting}
					onChange={(v) =>
						setState((prev) => ({
							...prev,
							url: v,
							errors: { ...prev.errors, url: "" },
						}))
					}
				/>

				<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
					<p className={HINT}>
						New assets land in the review queue with
						<span className="font-medium text-zinc-700"> Pending</span>
						{" "}status.
					</p>
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={state.submitting}
					>
						{state.submitting ? "Registering." : "Register asset"}
					</button>
				</div>
				{state.success && (
					<p className="text-xs text-green-700" role="status">
						{state.success}
					</p>
				)}
				{state.serverError && (
					<p className={ERROR_TEXT} role="alert">
						{state.serverError}
					</p>
				)}
			</form>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Field — small labeled input wrapper to avoid duplication.
// ---------------------------------------------------------------------------

function Field(props: {
	id: string;
	name: string;
	label: string;
	placeholder?: string;
	value: string;
	error?: string;
	disabled?: boolean;
	onChange: (v: string) => void;
	type?: string;
}) {
	const {
		id,
		name,
		label,
		placeholder,
		value,
		error,
		disabled,
		onChange,
		type,
	} = props;
	return (
		<div className="flex flex-col gap-1.5">
			<label htmlFor={id} className={LABEL}>
				{label}
			</label>
			<input
				id={id}
				name={name}
				type={type ?? "text"}
				placeholder={placeholder}
				className={INPUT}
				value={value}
				onChange={(e) => onChange(e.currentTarget.value)}
				aria-invalid={Boolean(error)}
				aria-describedby={error ? `${id}-error` : undefined}
				disabled={disabled}
			/>
			{error && (
				<p id={`${id}-error`} className={ERROR_TEXT}>
					{error}
				</p>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Gallery
// ---------------------------------------------------------------------------

interface GalleryState {
	rows: CreativeAsset[];
	loading: boolean;
	error: string | null;
	busyId: string | null;
}

const EMPTY_GALLERY: GalleryState = {
	rows: [],
	loading: true,
	error: null,
	busyId: null,
};

function GalleryCard(props: {
	advertiserId: string;
	onAdvertiserChange: (v: string) => void;
	refreshKey: number;
	triggerRefresh: () => void;
}) {
	const { advertiserId, onAdvertiserChange, refreshKey, triggerRefresh } = props;
	const [state, setState] = useState<GalleryState>(EMPTY_GALLERY);
	const [pendingId, setPendingId] = useState<string>("");

	useEffect(() => {
		if (!advertiserId) {
			setState((prev) => ({ ...prev, loading: false, rows: [], error: null }));
			return undefined;
		}
		let cancelled = false;
		setState((prev) => ({ ...prev, loading: true, error: null }));
		(async () => {
			try {
				const rows = await fetchAssets(advertiserId);
				if (cancelled) return;
				setState({ rows, loading: false, error: null, busyId: null });
			} catch (err) {
				if (cancelled) return;
				setState({
					rows: [],
					loading: false,
					busyId: null,
					error:
						err instanceof Error ? err.message : "Unable to load assets.",
				});
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [advertiserId, refreshKey]);

	const counts = useMemo(() => {
		return state.rows.reduce(
			(acc, row) => {
				if (row.approved) acc.approved += 1;
				else acc.pending += 1;
				return acc;
			},
			{ approved: 0, pending: 0 },
		);
	}, [state.rows]);

	async function onToggleApproval(asset: CreativeAsset) {
		setState((prev) => ({ ...prev, busyId: asset.id }));
		try {
			const updated = await setAssetApproval(asset.id, !asset.approved);
			setState((prev) => ({
				...prev,
				busyId: null,
				rows: prev.rows.map((r) => (r.id === updated.id ? updated : r)),
			}));
		} catch (err) {
			setState((prev) => ({
				...prev,
				busyId: null,
				error:
					err instanceof Error ? err.message : "Unable to toggle approval.",
			}));
		}
	}

	async function onDelete(asset: CreativeAsset) {
		setState((prev) => ({ ...prev, busyId: asset.id }));
		try {
			await deleteAsset(asset.id);
			setState((prev) => ({
				...prev,
				busyId: null,
				rows: prev.rows.filter((r) => r.id !== asset.id),
			}));
		} catch (err) {
			setState((prev) => ({
				...prev,
				busyId: null,
				error: err instanceof Error ? err.message : "Unable to delete asset.",
			}));
		}
	}

	return (
		<section aria-label="Assets gallery" className={`${CARD} flex flex-col gap-4 p-6`}>
			<header className="flex flex-col gap-3">
				<div className="flex items-center justify-between gap-2">
					<div className="flex flex-col gap-1">
						<h2 className="text-lg font-semibold text-zinc-900">
							Assets gallery
						</h2>
						<p className={HINT}>
							Pick an advertiser to view its registered assets and manage
							approval state.
						</p>
					</div>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={triggerRefresh}
						disabled={!advertiserId}
					>
						Refresh
					</button>
				</div>
				<form
					className="flex flex-col gap-2 sm:flex-row sm:items-end"
					aria-label="Filter gallery by advertiser"
					onSubmit={(e) => {
						e.preventDefault();
						onAdvertiserChange(pendingId.trim());
					}}
				>
					<div className="flex-1">
						<label
							htmlFor="adv-creative-gallery-id"
							className={LABEL}
						>
							Advertiser ID
						</label>
						<input
							id="adv-creative-gallery-id"
							type="text"
							className={INPUT}
							value={pendingId}
							onChange={(e) => setPendingId(e.currentTarget.value)}
							placeholder="adv_42"
							aria-label="Advertiser id to load"
						/>
					</div>
					<button type="submit" className={BTN_PRIMARY}>
						Load assets
					</button>
				</form>
				<dl
					className="flex flex-wrap gap-4 text-xs text-zinc-600"
					aria-label="Approval counts"
				>
					<div className="flex items-center gap-1">
						<dt className="font-medium text-zinc-500">Approved:</dt>
						<dd className="font-semibold text-green-700">
							{counts.approved}
						</dd>
					</div>
					<div className="flex items-center gap-1">
						<dt className="font-medium text-zinc-500">Pending:</dt>
						<dd className="font-semibold text-zinc-700">{counts.pending}</dd>
					</div>
				</dl>
			</header>

			<div
				className="overflow-x-auto rounded-[6px] border border-zinc-200"
				role="region"
				aria-label="Assets list"
			>
				<table className="min-w-full divide-y divide-zinc-200 text-sm">
					<thead className="bg-zinc-50 text-xs uppercase tracking-[0.08em] text-zinc-500">
						<tr>
							<th scope="col" className="px-3 py-2 text-left">
								Name
							</th>
							<th scope="col" className="px-3 py-2 text-left">
								Kind
							</th>
							<th scope="col" className="px-3 py-2 text-left">
								Size
							</th>
							<th scope="col" className="px-3 py-2 text-left">
								Dim
							</th>
							<th scope="col" className="px-3 py-2 text-left">
								Status
							</th>
							<th scope="col" className="px-3 py-2 text-right">
								Actions
							</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-zinc-100">
						{!advertiserId ? (
							<tr>
								<td
									colSpan={6}
									className="px-3 py-6 text-center text-zinc-500"
								>
									Enter an advertiser ID above to list its assets.
								</td>
							</tr>
						) : state.loading ? (
							<tr>
								<td
									colSpan={6}
									className="px-3 py-6 text-center text-zinc-500"
								>
									Loading assets.
								</td>
							</tr>
						) : state.rows.length === 0 ? (
							<tr>
								<td
									colSpan={6}
									className="px-3 py-6 text-center text-zinc-500"
								>
									{state.error ?? "No assets uploaded yet."}
								</td>
							</tr>
						) : (
							state.rows.map((row) => (
								<tr key={row.id}>
									<td className="px-3 py-2">
										<div className="flex flex-col gap-0.5">
											<span className="font-medium text-zinc-800">
												{row.name}
											</span>
											<a
												href={row.url}
												className="truncate font-mono text-[10px] text-zinc-500 hover:text-zinc-800"
												title={row.url}
												rel="noreferrer"
												target="_blank"
											>
												{row.url}
											</a>
										</div>
									</td>
									<td className="px-3 py-2 capitalize text-zinc-700">
										{row.kind}
									</td>
									<td className="px-3 py-2 text-zinc-600">
										{formatBytes(row.size_bytes)}
									</td>
									<td className="px-3 py-2 text-zinc-600">
										{formatDimensions(row.width, row.height)}
									</td>
									<td className="px-3 py-2">
										<span
											className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
												row.approved
													? "bg-green-50 text-green-700"
													: "bg-zinc-100 text-zinc-700"
											}`}
											aria-label={`Approval state ${approvalBadgeLabel(row.approved)}`}
										>
											{approvalBadgeLabel(row.approved)}
										</span>
									</td>
									<td className="px-3 py-2 text-right">
										<div className="flex items-center justify-end gap-2">
											<button
												type="button"
												className={BTN_SECONDARY}
												onClick={() => onToggleApproval(row)}
												disabled={state.busyId === row.id}
												aria-label={`${
													row.approved ? "Revoke" : "Approve"
												} ${row.name}`}
											>
												{row.approved ? "Revoke" : "Approve"}
											</button>
											<button
												type="button"
												className={BTN_DANGER}
												onClick={() => onDelete(row)}
												disabled={state.busyId === row.id}
												aria-label={`Delete ${row.name}`}
											>
												Delete
											</button>
										</div>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
			{state.error && advertiserId && state.rows.length > 0 && (
				<p className={ERROR_TEXT} role="alert">
					{state.error}
				</p>
			)}
		</section>
	);
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function DocHint() {
	return (
		<aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
			<p className="font-medium text-zinc-800">How approval works</p>
			<ul className="mt-2 flex list-inside list-disc flex-col gap-1">
				<li>
					Advertisers register metadata for assets already uploaded to object
					storage — the URL is produced by a presigned-upload stub.
				</li>
				<li>
					Internal ops review each asset and flip the approval state via
					{" "}
					<span className="font-mono text-zinc-700">
						POST /approve/:id
					</span>
					.
				</li>
				<li>
					Only approved assets can be attached to campaigns on downstream
					surfaces.
				</li>
			</ul>
		</aside>
	);
}
